import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Mail, Phone, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYEE_BUCKETS = [
  "1–10",
  "11–50",
  "51–200",
  "201–500",
  "501–1,000",
  "1,001–5,000",
  "5,001–10,000",
  "10,000+",
];

const INQUIRY_TYPES = [
  { value: "department", label: "Department-specific", desc: "A single team, function, or business unit." },
  { value: "company", label: "Company-wide", desc: "Cross-functional, enterprise-level engagement." },
  { value: "exploratory", label: "Exploratory", desc: "We're still scoping where the work would land." },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    work_email: "",
    company: "",
    role: "",
    phone: "",
    employee_count: "",
    inquiry_scope: "",
    department: "",
    message: "",
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState({ state: "idle", error: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "submitting") return;

    // Lightweight client-side validation (boundary)
    if (!form.name || !form.work_email || !form.company || !form.employee_count || !form.inquiry_scope || !form.message) {
      setStatus({ state: "error", error: "Please complete every required field." });
      return;
    }
    if (!consent) {
      setStatus({ state: "error", error: "Please review and accept the Privacy Policy to continue." });
      return;
    }

    setStatus({ state: "submitting", error: "" });
    try {
      await axios.post(`${API}/contact/submit`, form);
      setStatus({ state: "success", error: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or email info@itsthefreedominme.com.";
      setStatus({ state: "error", error: msg });
    }
  };

  if (status.state === "success") {
    return (
      <main data-testid="page-contact-success" className="pt-20 min-h-screen bg-[var(--bone)]">
        <section className="max-w-3xl mx-auto px-6 lg:px-10 py-32 lg:py-40 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--gold)] text-[var(--navy-950)]">
            <CheckCircle2 size={28} />
          </div>
          <div className="eyebrow mt-10">Message Received</div>
          <h1 className="font-display text-[40px] md:text-[56px] leading-[1.04] text-[var(--navy-900)] mt-6">
            Thank you. We’ll be in touch within<br /> two business days.
          </h1>
          <p className="mt-8 text-[16px] leading-[1.7] text-[var(--muted)]">
            In the meantime, you can take the A.L.I.G.N.™ Readiness Index — it returns a five-pillar
            executive report you’ll find useful before our conversation.
          </p>
          <Link
            to="/assessment"
            data-testid="contact-success-cta-assessment"
            className="mt-12 btn-gold px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2"
          >
            Take the 5-Min Diagnostic <ArrowRight size={16} />
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="page-contact" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div
          className="absolute -top-32 -right-40 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(30,90,176,0.30), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 lg:pt-36 lg:pb-24">
          <div className="eyebrow-light rise rise-1">Contact</div>
          <h1 className="font-display text-[44px] md:text-[68px] leading-[0.98] mt-6 rise rise-2 max-w-4xl">
            Tell Us What’s Out Of Alignment.<br />
            <span className="text-[var(--gold-soft)]">We’ll Help You Map The Path Forward.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[18px] leading-[1.55] text-[var(--navy-100)]/85 rise rise-3">
            Every engagement starts with a conversation. Share the context and a member of the Freedom In Me
            team will respond within two business days.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section className="bg-[var(--bone)] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left column — context + direct details */}
          <aside className="lg:col-span-4 space-y-10">
            <div>
              <div className="eyebrow">Direct</div>
              <ul className="mt-6 space-y-5 text-[14px] leading-[1.6] text-[var(--navy-900)]">
                <li className="flex items-start gap-3">
                  <Mail size={18} className="mt-0.5 text-[var(--gold)] flex-shrink-0" />
                  <a href="mailto:info@itsthefreedominme.com" data-testid="contact-direct-email" className="link-underline text-[14px] tracking-normal normal-case">
                    info@itsthefreedominme.com
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5 text-[var(--gold)] flex-shrink-0" />
                  <span>United States · Engagements served nationally and internationally.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={18} className="mt-0.5 text-[var(--gold)] flex-shrink-0" />
                  <span className="text-[var(--muted)]">By appointment — scheduled after your inquiry is reviewed.</span>
                </li>
              </ul>
            </div>

            <div className="hairline"></div>

            <div>
              <div className="eyebrow">What Happens Next</div>
              <ol className="mt-6 space-y-4 text-[13px] leading-[1.65] text-[var(--muted)]">
                {[
                  ["Review", "Your inquiry is reviewed within two business days."],
                  ["Discovery call", "30 minutes — we listen first, then map the friction."],
                  ["Proposal", "A clear scope, structure, and outcome — tailored to your org."],
                ].map(([t, d], i) => (
                  <li key={t} className="grid grid-cols-[auto_1fr] gap-4">
                    <span className="font-display text-[var(--gold)] text-[20px] leading-none">0{i + 1}</span>
                    <div>
                      <div className="text-[12px] tracking-[0.22em] uppercase text-[var(--navy-900)] font-semibold">{t}</div>
                      <div className="mt-1">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Right column — form */}
          <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-8 bg-white border border-[var(--navy-900)]/8 p-8 lg:p-12">
            <div className="eyebrow">Send a Message</div>
            <h2 className="font-display text-[28px] md:text-[36px] leading-[1.1] text-[var(--navy-900)] mt-4">
              Start with the right context.
            </h2>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              <Field label="Full name *" testid="contact-input-name">
                <input value={form.name} onChange={update("name")} className={inputCls} placeholder="Jane Doe" />
              </Field>
              <Field label="Work email *" testid="contact-input-email">
                <input type="email" value={form.work_email} onChange={update("work_email")} className={inputCls} placeholder="jane@company.com" />
              </Field>
              <Field label="Company *" testid="contact-input-company">
                <input value={form.company} onChange={update("company")} className={inputCls} placeholder="Acme Corp" />
              </Field>
              <Field label="Role / Title" testid="contact-input-role">
                <input value={form.role} onChange={update("role")} className={inputCls} placeholder="COO, Director of Operations, etc." />
              </Field>
              <Field label="Phone (optional)" testid="contact-input-phone">
                <input value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(555) 555-5555" />
              </Field>
              <Field label="Number of employees *" testid="contact-input-employees">
                <select value={form.employee_count} onChange={update("employee_count")} className={inputCls}>
                  <option value="">Select range…</option>
                  {EMPLOYEE_BUCKETS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Inquiry scope — radio cards */}
            <div className="mt-10">
              <div className="text-[12px] tracking-[0.22em] uppercase text-[var(--navy-900)] font-semibold">Inquiry scope *</div>
              <p className="mt-1 text-[12px] text-[var(--muted)]">Help us route your inquiry to the right person on the team.</p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3" role="radiogroup">
                {INQUIRY_TYPES.map((t) => {
                  const selected = form.inquiry_scope === t.value;
                  return (
                    <button
                      type="button"
                      key={t.value}
                      data-testid={`contact-inquiry-${t.value}`}
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setForm({ ...form, inquiry_scope: t.value })}
                      className={`text-left p-5 border transition-all duration-200 ${
                        selected
                          ? "border-[var(--navy-900)] bg-[var(--navy-900)] text-[var(--bone)]"
                          : "border-[var(--navy-900)]/15 bg-white text-[var(--navy-900)] hover:border-[var(--navy-900)]/40"
                      }`}
                    >
                      <div className="text-[13px] font-semibold tracking-tight">{t.label}</div>
                      <div className={`text-[11px] leading-[1.5] mt-2 ${selected ? "text-[var(--bone)]/75" : "text-[var(--muted)]"}`}>{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Department field — only shown when inquiry_scope = department */}
            {form.inquiry_scope === "department" && (
              <div className="mt-6" data-testid="contact-department-wrapper">
                <Field label="Which department? *" testid="contact-input-department">
                  <input value={form.department} onChange={update("department")} className={inputCls} placeholder="Operations, HR, IT, Finance, Workforce Programs, etc." />
                </Field>
              </div>
            )}

            <div className="mt-10">
              <Field label="Tell us what's out of alignment *" testid="contact-input-message">
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className={`${inputCls} resize-y min-h-[140px]`}
                  placeholder="Describe the friction, the goal, and any context that would help us understand the engagement."
                />
              </Field>
            </div>

            {status.state === "error" && (
              <div data-testid="contact-error" className="mt-6 p-4 bg-red-50 border border-red-200 text-[13px] leading-[1.5] text-red-800">
                {status.error}
              </div>
            )}

            {/* Confidentiality reassurance + consent */}
            <div className="mt-10 p-5 bg-[var(--navy-50)] border border-[var(--navy-900)]/8 text-[12px] leading-[1.65] text-[var(--muted)]">
              <strong className="text-[var(--navy-900)]">Your information is confidential.</strong>{" "}
              Inquiry details are reviewed only by authorized members of the Freedom In Me team and used solely to respond to you. We do not sell, share, or rent your information. See our{" "}
              <Link to="/privacy" className="underline underline-offset-2 text-[var(--navy-900)] hover:text-[var(--gold)]">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/security" className="underline underline-offset-2 text-[var(--navy-900)] hover:text-[var(--gold)]">Security &amp; Trust</Link> page.
            </div>

            <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="contact-consent-wrapper">
              <input
                type="checkbox"
                data-testid="contact-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[var(--navy-900)] cursor-pointer"
              />
              <span className="text-[12px] leading-[1.6] text-[var(--navy-900)]">
                I have read and agree to the{" "}
                <Link to="/privacy" className="underline underline-offset-2 hover:text-[var(--gold)]">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/terms" className="underline underline-offset-2 hover:text-[var(--gold)]">Terms of Use</Link>, and I consent to be contacted by the Freedom In Me team about my inquiry.
              </span>
            </label>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status.state === "submitting"}
                className="btn-primary px-8 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold rounded-full inline-flex items-center gap-2 disabled:opacity-60"
              >
                {status.state === "submitting" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Inquiry <ArrowRight size={16} />
                  </>
                )}
              </button>
              <p className="text-[11px] text-[var(--muted)] max-w-xs">
          By submitting you consent to be contacted by the Freedom In Me team about your inquiry.
              </p>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-wrapper` : undefined}>
      <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--navy-900)] font-semibold">{label}</span>
      <span className="block mt-2">{children}</span>
    </label>
  );
}

const inputCls =
  "w-full px-4 py-3 bg-[var(--navy-50)] border border-[var(--navy-900)]/10 outline-none text-[14px] text-[var(--navy-900)] focus:border-[var(--navy-900)] transition-colors";
